import type {
    CarWashState,
    Customer,
    Invoice,
    Job,
    JobStatus,
    Service,
    Staff,
    StaffStatus,
    Vehicle
} from '@/types'

import {
    LONG_BREAK_MINUTES,
    LONG_WAIT_MINUTES,
    OVERDUE_GRACE_MINUTES
} from '@/constants/carwash'
import { isActiveJob, isWorkingJob } from '@/lib/carwash/engine'
import {
    MINUTE,
    addDays,
    formatMinutes,
    startOfDay,
    toTime
} from '@/lib/carwash/format'
import { plateLabel } from '@/lib/carwash/plate'

/* Lookups ----------------------------------------------------------- */

export type Lookups = {
    services: Map<string, Service>
    staff: Map<string, Staff>
    vehicles: Map<string, Vehicle>
    customers: Map<string, Customer>
    invoices: Map<string, Invoice>
    jobs: Map<string, Job>
    jobsByVehicle: Map<string, Job[]>
}

const byNewest = (a: Job, b: Job): number => toTime(b.enteredAt) - toTime(a.enteredAt)

export const createLookups = (state: CarWashState): Lookups => {
    const jobsByVehicle = new Map<string, Job[]>()

    for (const job of state.jobs) {
        const list = jobsByVehicle.get(job.vehicleId)
        if (list) list.push(job)
        else jobsByVehicle.set(job.vehicleId, [job])
    }

    for (const list of jobsByVehicle.values()) list.sort(byNewest)

    return {
        services: new Map(state.services.map(item => [item.id, item])),
        staff: new Map(state.staff.map(item => [item.id, item])),
        vehicles: new Map(state.vehicles.map(item => [item.id, item])),
        customers: new Map(state.customers.map(item => [item.id, item])),
        invoices: new Map(state.invoices.map(item => [item.id, item])),
        jobs: new Map(state.jobs.map(item => [item.id, item])),
        jobsByVehicle
    }
}

export const getJobCustomer = (job: Job, lookups: Lookups): Customer | null => {
    const vehicle = lookups.vehicles.get(job.vehicleId)
    const id = job.customerId ?? vehicle?.customerId ?? null
    return id ? lookups.customers.get(id) ?? null : null
}

/* Time -------------------------------------------------------------- */

export const getElapsedMs = (job: Job, now: number): number => {
    if (!job.startedAt) return 0

    const end = job.completedAt
        ? toTime(job.completedAt)
        : job.cancelledAt ? toTime(job.cancelledAt) : now

    return Math.max(0, end - toTime(job.startedAt))
}

export const getWaitMs = (job: Job, now: number): number => {
    const end = job.startedAt
        ? toTime(job.startedAt)
        : job.cancelledAt ? toTime(job.cancelledAt) : now

    return Math.max(0, end - toTime(job.enteredAt))
}

export const getOverrunMinutes = (job: Job, now: number): number => getElapsedMs(job, now) / MINUTE - job.estimatedMinutes

export const getRemainingMinutes = (job: Job, now: number): number => Math.max(0, job.estimatedMinutes - getElapsedMs(job, now) / MINUTE)

export const getProgress = (job: Job, now: number): number => {
    if (!job.estimatedMinutes) return 0
    return Math.min(1, getElapsedMs(job, now) / (job.estimatedMinutes * MINUTE))
}

/* Jobs -------------------------------------------------------------- */

const STATUS_PRIORITY: Record<JobStatus, number> = {
    quality_check: 0,
    washing: 1,
    assigned: 2,
    waiting: 3,
    completed: 4,
    cancelled: 5
}

const jobClock = (job: Job): number => toTime(job.startedAt ?? job.assignedAt ?? job.enteredAt)

export const sortActiveJobs = (jobs: Job[]): Job[] => [...jobs].sort((a, b) => {
    const priority = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status]
    return priority || jobClock(a) - jobClock(b)
})

export const getActiveJobs = (state: CarWashState): Job[] => sortActiveJobs(state.jobs.filter(isActiveJob))

export const getVehicleVisits = (vehicleId: string, lookups: Lookups): Job[] => lookups.jobsByVehicle.get(vehicleId) ?? []

/* Staff ------------------------------------------------------------- */

export const getStaffStatus = (member: Staff, hasWork: boolean): StaffStatus => {
    if (member.duty === 'inactive') return 'inactive'
    if (hasWork) return 'busy'
    if (member.duty === 'on_break') return 'break'
    return 'available'
}

export type StaffBoardEntry = {
    staff: Staff
    status: StaffStatus
    jobs: Job[]
    currentJob: Job | null
    completedToday: number
    revenueToday: number
    freeInMinutes: number | null
    idleSince: number | null
    breakPending: boolean
}

const STAFF_ORDER: Record<StaffStatus, number> = {
    available: 0,
    busy: 1,
    break: 2,
    inactive: 3
}

export const buildStaffBoard = (state: CarWashState, now: number): StaffBoardEntry[] => {
    const dayStart = startOfDay(now).getTime()
    const invoiceTotals = new Map(state.invoices.map(invoice => [invoice.id, invoice.total]))

    const entries = state.staff.map((member): StaffBoardEntry => {
        const jobs = state.jobs
            .filter(job => job.staffId === member.id && isWorkingJob(job))
            .sort((a, b) => jobClock(a) - jobClock(b))

        let completedToday = 0
        let revenueToday = 0
        let lastFinished = 0

        for (const job of state.jobs) {
            if (job.staffId !== member.id || job.status !== 'completed' || !job.completedAt) continue

            const finishedAt = toTime(job.completedAt)
            if (finishedAt > lastFinished) lastFinished = finishedAt

            if (finishedAt >= dayStart) {
                completedToday += 1
                revenueToday += job.invoiceId ? invoiceTotals.get(job.invoiceId) ?? job.price : job.price
            }
        }

        const status = getStaffStatus(member, jobs.length > 0)
        const freeInMinutes = jobs.length
            ? Math.ceil(jobs.reduce((total, job) => total + getRemainingMinutes(job, now), 0))
            : null

        return {
            staff: member,
            status,
            jobs,
            currentJob: jobs[0] ?? null,
            completedToday,
            revenueToday,
            freeInMinutes,
            idleSince: status === 'available' ? Math.max(lastFinished, toTime(member.dutySince)) : null,
            breakPending: member.duty === 'on_break' && jobs.length > 0
        }
    })

    return entries.sort((a, b) => {
        const order = STAFF_ORDER[a.status] - STAFF_ORDER[b.status]
        if (order) return order
        if (a.status === 'available') return (a.idleSince ?? 0) - (b.idleSince ?? 0)
        if (a.status === 'busy') return (a.freeInMinutes ?? 0) - (b.freeInMinutes ?? 0)
        return a.staff.name.localeCompare(b.staff.name)
    })
}

/* Today ------------------------------------------------------------- */

export type DayRange = { start: number, end: number }

export const getDayRange = (now: number, offsetDays = 0): DayRange => {
    const start = addDays(startOfDay(now), offsetDays)
    return { start: start.getTime(), end: addDays(start, 1).getTime() }
}

export const inRange = (value: string | null, range: DayRange): boolean => {
    if (!value) return false
    const time = toTime(value)
    return time >= range.start && time < range.end
}

export type TodayStats = {
    vehiclesToday: number
    inProgress: number
    waiting: number
    completedToday: number
    sales: number
    collected: number
    billsToday: number
    paidToday: number
    pendingCount: number
    pendingAmount: number
    pendingTodayCount: number
    collectedByMethod: Record<string, number>
}

export const getTodayStats = (state: CarWashState, now: number): TodayStats => {
    const today = getDayRange(now)
    const collectedByMethod: Record<string, number> = { cash: 0, upi: 0, card: 0, other: 0 }

    let vehiclesToday = 0
    let inProgress = 0
    let waiting = 0
    let completedToday = 0

    for (const job of state.jobs) {
        if (job.status !== 'cancelled' && inRange(job.enteredAt, today)) vehiclesToday += 1
        if (isWorkingJob(job)) inProgress += 1
        if (job.status === 'waiting') waiting += 1
        if (job.status === 'completed' && inRange(job.completedAt, today)) completedToday += 1
    }

    let sales = 0
    let billsToday = 0
    let paidToday = 0
    let pendingCount = 0
    let pendingAmount = 0
    let pendingTodayCount = 0

    for (const invoice of state.invoices) {
        const issuedToday = inRange(invoice.issuedAt, today)

        if (issuedToday) {
            sales += invoice.total
            billsToday += 1
            if (invoice.status === 'paid') paidToday += 1
        }

        if (invoice.status === 'pending') {
            pendingCount += 1
            pendingAmount += invoice.total
            if (issuedToday) pendingTodayCount += 1
        }
    }

    let collected = 0

    for (const payment of state.payments) {
        if (payment.voidedAt || !inRange(payment.receivedAt, today)) continue
        collected += payment.amount
        collectedByMethod[payment.method] = (collectedByMethod[payment.method] ?? 0) + payment.amount
    }

    return {
        vehiclesToday,
        inProgress,
        waiting,
        completedToday,
        sales,
        collected,
        billsToday,
        paidToday,
        pendingCount,
        pendingAmount,
        pendingTodayCount,
        collectedByMethod
    }
}

/* Alerts ------------------------------------------------------------ */

export type AlertTone = 'red' | 'orange' | 'amber'

export type Alert = {
    id: string
    tone: AlertTone
    title: string
    detail: string
    href: string
}

export const getAlerts = (state: CarWashState, now: number, lookups: Lookups): Alert[] => {
    const alerts: Alert[] = []
    const active = getActiveJobs(state)
    const board = buildStaffBoard(state, now)
    const available = board.filter(entry => entry.status === 'available')
    const waitingJobs = active.filter(job => job.status === 'waiting')

    for (const job of active) {
        if (job.status !== 'washing' && job.status !== 'quality_check') continue

        const overrun = getOverrunMinutes(job, now)
        if (overrun < OVERDUE_GRACE_MINUTES) continue

        const staffName = job.staffId ? lookups.staff.get(job.staffId)?.name : null

        alerts.push({
            id: `overdue-${job.id}`,
            tone: 'red',
            title: `${plateLabel(job.plate)} is ${formatMinutes(overrun)} over time`,
            detail: `${job.serviceName}${staffName ? ` with ${staffName}` : ''}, estimated ${formatMinutes(job.estimatedMinutes)}`,
            href: `/vehicles/${job.id}`
        })
    }

    if (waitingJobs.length && available.length) {
        const first = waitingJobs[0]
        const vehicles = waitingJobs.length === 1 ? '1 vehicle is' : `${waitingJobs.length} vehicles are`
        const staff = available.length === 1 ? `${available[0].staff.name} is free` : `${available.length} staff are free`

        alerts.push({
            id: 'queue-ready',
            tone: 'orange',
            title: `${vehicles} waiting and ${staff}`,
            detail: `Assign ${plateLabel(first.plate)} to start the wash.`,
            href: `/vehicles/${first.id}`
        })
    }

    for (const job of waitingJobs) {
        const waited = getWaitMs(job, now) / MINUTE
        if (waited < LONG_WAIT_MINUTES) continue

        alerts.push({
            id: `waiting-${job.id}`,
            tone: 'orange',
            title: `${plateLabel(job.plate)} has waited ${formatMinutes(waited)}`,
            detail: `${job.serviceName}, entered the queue and still has no staff.`,
            href: `/vehicles/${job.id}`
        })
    }

    const today = getDayRange(now)
    const pendingToday = state.invoices.filter(invoice => invoice.status === 'pending' && inRange(invoice.issuedAt, today))

    if (pendingToday.length) {
        alerts.push({
            id: 'pending-bills',
            tone: 'amber',
            title: pendingToday.length === 1 ? '1 bill from today is unpaid' : `${pendingToday.length} bills from today are unpaid`,
            detail: 'Collect payment before the vehicle leaves.',
            href: '/billing?tab=pending'
        })
    }

    for (const entry of board) {
        if (entry.status !== 'break') continue

        const minutes = (now - toTime(entry.staff.dutySince)) / MINUTE
        if (minutes < LONG_BREAK_MINUTES) continue

        alerts.push({
            id: `break-${entry.staff.id}`,
            tone: 'amber',
            title: `${entry.staff.name} has been on break for ${formatMinutes(minutes)}`,
            detail: 'Set them back to available when they return.',
            href: `/staff/${entry.staff.id}`
        })
    }

    return alerts
}

/* Customers and vehicles -------------------------------------------- */

export type VehicleSummary = {
    vehicle: Vehicle
    customer: Customer | null
    visits: number
    totalSpent: number
    lastVisit: Job | null
    firstVisitAt: string | null
    favouriteService: string | null
}

export const summarizeVehicle = (vehicle: Vehicle, lookups: Lookups): VehicleSummary => {
    const visits = getVehicleVisits(vehicle.id, lookups).filter(job => job.status !== 'cancelled')
    const counts = new Map<string, number>()
    let totalSpent = 0

    for (const job of visits) {
        counts.set(job.serviceName, (counts.get(job.serviceName) ?? 0) + 1)
        const invoice = job.invoiceId ? lookups.invoices.get(job.invoiceId) : undefined
        if (invoice) totalSpent += invoice.total
    }

    let favouriteService: string | null = null
    let best = 0

    for (const [name, count] of counts) {
        if (count > best) {
            best = count
            favouriteService = name
        }
    }

    return {
        vehicle,
        customer: vehicle.customerId ? lookups.customers.get(vehicle.customerId) ?? null : null,
        visits: visits.length,
        totalSpent,
        lastVisit: visits[0] ?? null,
        firstVisitAt: visits.length ? visits[visits.length - 1].enteredAt : null,
        favouriteService
    }
}
